import { useMutation, useSubscription } from '@apollo/client/react';
import { closeSnackbar } from 'notistack';

import { AppointmentSnackbar } from '@features/Remote/constants';
import {
  Appointment,
  AppointmentEvent,
  AppointmentParticipantType,
  AppointmentType,
  ControlGivenEvent,
  ControlRequestedEvent,
} from '@generated/gql/graphql';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { EventTypes, emitter } from '@services/emitter';
import {
  appointmentAcknowledgeHeartbeat,
  appointmentEvent,
} from '@utils/queries/appointment';
import {
  isBasketUpdatedEvent,
  isControlDeniedEvent,
  isControlGivenEvent,
  isControlRequestedEvent,
  isHeartbeatEvent,
  isParticipantJoinedAppointmentEvent,
  isParticipantLeftAppointmentEvent,
  isStateChangedEvent,
} from '@utils/typeCheck';

interface UseAppointmentEventHandlerParams {
  appointmentId: Nullable<string>;
  userId: Nullable<string>;
  appointment: Nullable<Appointment>;
  controlRequest: Nullable<ControlRequestedEvent>;
  setControlGiven: (event: Nullable<ControlGivenEvent>) => void;
  setControlRequest: (event: Nullable<ControlRequestedEvent>) => void;
  snackbar: ReturnType<typeof useSnackbar>;
  clearStorage: () => void;
}

const useAppointmentEventHandler = ({
  appointmentId,
  userId,
  appointment,
  controlRequest,
  setControlGiven,
  setControlRequest,
  snackbar,
  clearStorage,
}: UseAppointmentEventHandlerParams) => {
  const [ackHeartbeat] = useMutation(appointmentAcknowledgeHeartbeat);

  useSubscription(appointmentEvent, {
    variables: {
      id: appointmentId ?? '',
    },
    skip: !appointmentId,
    onData: ({ data }) => {
      const event = data.data?.appointmentEvent as AppointmentEvent;
      if (!event) return;

      if (isControlGivenEvent(event)) {
        setControlGiven(event);

        if (
          event.toParticipant.id === controlRequest?.fromParticipant.id &&
          event.fromParticipant.id === controlRequest?.toParticipant.id
        ) {
          setControlRequest(null);
        }
      }

      if (isControlDeniedEvent(event)) {
        if (
          event.toParticipant.id === controlRequest?.fromParticipant.id &&
          event.fromParticipant.id === controlRequest?.toParticipant.id
        ) {
          setControlRequest(null);
        }

        if (event.toParticipant.id === userId) {
          snackbar({
            message: `${event.fromParticipant.name} denied your control request`,
            variant: SnackbarVariant.Error,
            preventDuplicate: true,
          });
        }
      }

      if (isControlRequestedEvent(event)) {
        setControlRequest(event);
      }

      if (isParticipantJoinedAppointmentEvent(event)) {
        if (event.participant.type === AppointmentParticipantType.Customer) {
          closeSnackbar(AppointmentSnackbar.CUSTOMER_LEFT);
        }

        snackbar({
          message: `${event.participant.name} has joined your appointment`,
          key: AppointmentSnackbar.CUSTOMER_JOINED,
          variant: SnackbarVariant.Info,
          preventDuplicate: true,
          persist: true,
        });
      }

      if (isParticipantLeftAppointmentEvent(event)) {
        const hasUserLeft = event.participant.id === userId;

        if (appointment?.type === AppointmentType.FaceToFace) {
          return;
        }
        const isPartner = [
          AppointmentParticipantType.Partner,
          AppointmentParticipantType.SupportingPartner,
        ].includes(event.participant.type);

        if (hasUserLeft) {
          if (!isPartner) {
            snackbar({
              message:
                'You have disconnected from the appointment. Your partner can share the appointment details with you again if you wish to rejoin',
              variant: SnackbarVariant.Error,
              preventDuplicate: true,
              persist: true,
            });
          }
          clearStorage();
          return;
        }

        snackbar({
          message: `${event.participant.name} has left the appointment`,
          variant: SnackbarVariant.Error,
          key: AppointmentSnackbar.CUSTOMER_LEFT,
          preventDuplicate: true,
          persist: true,
        });
      }

      if (isBasketUpdatedEvent(event)) {
        if (appointment?.controller?.id !== userId) {
          emitter.emit(EventTypes.RemoteBasketUpdated, true);
        }
      }

      if (
        isHeartbeatEvent(event) &&
        event.participantId === userId &&
        appointmentId
      ) {
        ackHeartbeat({
          variables: {
            id: appointmentId,
          },
        });
      }

      if (isStateChangedEvent(event)) {
        emitter.emit(EventTypes.RemoteStateChanged, {
          key: event.key,
          data: event.data,
        });
      }
    },
  });
};

export { useAppointmentEventHandler };
